import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import SessionMetadataEditorForm from './SessionMetadataEditorForm';
import { fetchDistinctTags, updateSessionMetadata } from '../../services/training';
import { isRichContentEmpty } from '../../utils/richText';

export default function SessionMetadataPanel({ session, canEdit, onMetadataUpdated }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(session.title);
  const [summary, setSummary] = useState(session.summary);
  const [transcript, setTranscript] = useState(session.transcript ?? '');
  const [speakerName, setSpeakerName] = useState(session.speakerName ?? '');
  const [tags, setTags] = useState(session.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [level, setLevel] = useState(session.levelValue ?? 'both');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) {
      setTitle(session.title);
      setSummary(session.summary);
      setTranscript(session.transcript ?? '');
      setSpeakerName(session.speakerName ?? '');
      setTags(session.tags ?? []);
      setLevel(session.levelValue ?? 'both');
    }
  }, [session, editing]);

  useEffect(() => {
    fetchDistinctTags().then(setTagSuggestions).catch(() => {});
  }, []);

  const handleCancel = () => {
    setEditing(false);
    setError('');
    setTitle(session.title);
    setSummary(session.summary);
    setTranscript(session.transcript ?? '');
    setSpeakerName(session.speakerName ?? '');
    setTags(session.tags ?? []);
    setLevel(session.levelValue ?? 'both');
    setTagInput('');
  };

  const handleSave = async () => {
    setError('');
    if (!title.trim()) {
      setError('El título es obligatorio');
      return;
    }
    if (isRichContentEmpty(summary)) {
      setError('El resumen es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const updated = await updateSessionMetadata(session.id, {
        title: title.trim(),
        summary: summary.trim(),
        transcript: transcript ?? '',
        speakerName: speakerName.trim() || null,
        tags,
        level,
      });
      onMetadataUpdated?.(updated);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) return null;

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="text-xs text-[var(--text-secondary)]">
          Metadatos de la sesión (el video no se puede cambiar aquí)
        </p>
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green flex-shrink-0"
          >
            <SafeIcon name="Pencil" className="w-3.5 h-3.5" />
            Editar metadatos
          </button>
        ) : (
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-main)]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 gfi-gradient-bg text-brand-navy px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? (
                <>
                  <SafeIcon name="Loader" className="w-3.5 h-3.5 animate-spin" />
                  Guardando…
                </>
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {editing && (
        <div className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] mb-6">
          <SessionMetadataEditorForm
            title={title}
            summary={summary}
            transcript={transcript}
            speakerName={speakerName}
            tags={tags}
            tagInput={tagInput}
            level={level}
            tagSuggestions={tagSuggestions}
            disabled={saving}
            onTitleChange={setTitle}
            onSummaryChange={setSummary}
            onTranscriptChange={setTranscript}
            onSpeakerNameChange={setSpeakerName}
            onTagsChange={setTags}
            onTagInputChange={setTagInput}
            onLevelChange={setLevel}
          />
        </div>
      )}
    </div>
  );
}
